/**
 * Sign-in — the console's front door.
 *
 * Structurally the OE console's Login: a dark brand hero on the left, a themed
 * auth card on the right, collapsing to a slim header band under 900px. The copy
 * is this product's, because this console does a different job.
 *
 * Authentication is SRP against the user pool (lib/cognito.ts), not a redirect to
 * the Hosted UI — that page's markup is AWS's and cannot be made to look like this.
 *
 * Three states share the card: sign-in, the first-run "set a password" challenge
 * Cognito raises for an admin-created user, and self-service reset.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';

import vinferMark from '../assets/vinfer-mark.svg';
import {
  AuthError,
  clearPendingChallenge,
  completeNewPassword,
  confirmPassword,
  forgotPassword,
  signIn,
} from '../lib/cognito';
import '../styles/login.css';

type Mode = 'signin' | 'new_password' | 'reset_request' | 'reset_confirm';

const COPY: Record<Mode, { title: string; intro: string; submit: string }> = {
  signin: {
    title: 'Sign in',
    intro: 'Client reports, schedules and delivery history.',
    submit: 'Sign in',
  },
  new_password: {
    title: 'Set your password',
    intro: 'This account was created for you. Choose a password to finish.',
    submit: 'Set password and continue',
  },
  reset_request: {
    title: 'Reset your password',
    intro: "Enter your email and we'll send a six-digit code.",
    submit: 'Send the code',
  },
  reset_confirm: {
    title: 'Choose a new password',
    intro: 'Enter the code from your email and a new password.',
    submit: 'Save and sign in',
  },
};

export default function Login() {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
  }, [mode]);

  // Backing out of the challenge must not leave a half-finished one behind.
  useEffect(() => {
    if (mode === 'signin') clearPendingChallenge();
  }, [mode]);

  function fail(err: unknown) {
    setError(err instanceof AuthError ? err.message : 'Something went wrong. Try again.');
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'signin') {
        const outcome = await signIn(email, password);
        if (outcome.status === 'new_password_required') {
          setPassword('');
          setMode('new_password');
          setNotice('Choose a password to finish setting up this account.');
        }
        // On success the session is installed and the guard swaps this page out.
      } else if (mode === 'new_password') {
        await completeNewPassword(newPassword);
      } else if (mode === 'reset_request') {
        await forgotPassword(email);
        setMode('reset_confirm');
        setNotice('If that address has an account, a code is on its way.');
      } else {
        await confirmPassword(email, code, newPassword);
        setMode('signin');
        setCode('');
        setNewPassword('');
        setNotice('Password changed. Sign in with it.');
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const copy = COPY[mode];
  const showEmail = mode === 'signin' || mode === 'reset_request' || mode === 'reset_confirm';
  const showPassword = mode === 'signin';
  const showNewPassword = mode === 'new_password' || mode === 'reset_confirm';
  const showCode = mode === 'reset_confirm';

  return (
    <div className="lg-root">
      <aside className="lg-hero">
        <div className="lg-hero-grid" aria-hidden="true" />
        <div className="lg-hero-glow" aria-hidden="true" />

        <div className="lg-lockup">
          <span className="lg-lockup-mark">
            <img src={vinferMark} alt="" width={30} height={30} />
          </span>
          <div>
            <div className="lg-lockup-name">Report Dispatch</div>
            <div className="lg-lockup-sub">by VInfer.AI</div>
          </div>
        </div>

        <div className="lg-hero-copy">
          <p className="lg-hero-title">Every client report, delivered and accounted for.</p>
          <p className="lg-hero-sub">
            Scheduled borrower and call reports for lending operations — generated, delivered,
            and traceable down to who opened which link.
          </p>
        </div>

        <div className="lg-hero-trust">
          <span>Download links expire in 7 days</span>
          <span>Every download audited</span>
          <span>All times IST</span>
        </div>
      </aside>

      <main className="lg-pane">
        <form className="lg-card" onSubmit={onSubmit} noValidate>
          <h1 className="lg-title">{copy.title}</h1>
          <p className="lg-intro">{copy.intro}</p>

          {error && (
            <div className="lg-alert" role="alert">
              {error}
            </div>
          )}
          {!error && notice && (
            <div className="lg-alert tone-ok" role="status">
              {notice}
            </div>
          )}

          {showEmail && (
            <div className="lg-field">
              <label className="lg-label" htmlFor="lg-email">
                Email
              </label>
              <input
                id="lg-email"
                ref={firstField}
                className="lg-input"
                type="email"
                name="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                disabled={busy || mode === 'reset_confirm'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@vinfer.ai"
              />
            </div>
          )}

          {showCode && (
            <div className="lg-field">
              <label className="lg-label" htmlFor="lg-code">
                Code from your email
              </label>
              <input
                id="lg-code"
                className="lg-input mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                disabled={busy}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </div>
          )}

          {showPassword && (
            <div className="lg-field">
              <label className="lg-label" htmlFor="lg-password">
                Password
              </label>
              <input
                id="lg-password"
                className="lg-input"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                disabled={busy}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}

          {showNewPassword && (
            <div className="lg-field">
              <label className="lg-label" htmlFor="lg-new-password">
                New password
              </label>
              <input
                id="lg-new-password"
                ref={mode === 'new_password' ? firstField : undefined}
                className="lg-input"
                type="password"
                name="new-password"
                autoComplete="new-password"
                required
                disabled={busy}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="lg-hint">
                At least 12 characters, with an uppercase letter, a number and a symbol.
              </p>
            </div>
          )}

          <button className="lg-submit" type="submit" disabled={busy}>
            {busy ? 'Working…' : copy.submit}
          </button>

          <div className="lg-row">
            {mode === 'signin' ? (
              <button
                className="lg-link"
                type="button"
                onClick={() => {
                  setMode('reset_request');
                  setError(null);
                  setNotice(null);
                }}
              >
                Forgot your password?
              </button>
            ) : (
              <button
                className="lg-link"
                type="button"
                onClick={() => {
                  setMode('signin');
                  setError(null);
                  setNotice(null);
                }}
              >
                Back to sign in
              </button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
